/**
 * Authenticated API Client
 *
 * Wrapper around fetch that automatically includes authentication tokens
 * in API requests to backend endpoints.
 */

import { supabase } from './supabaseClient';

/**
 * Get current auth token from Supabase
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error || !session) {
    console.error('Failed to get auth session:', error);
    return null;
  }

  return session.access_token;
}

/**
 * Make an authenticated API request
 *
 * @param endpoint - API endpoint path (e.g., '/api/elevenlabs-call')
 * @param options - Fetch options
 * @returns Fetch response
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  // Get auth token
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in.');
  }

  // Merge headers with auth token
  // Use custom header because Azure SWA replaces the Authorization header
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
    'X-Supabase-Auth': token
  };

  // Make request
  return fetch(endpoint, {
    ...options,
    headers
  });
}

/**
 * POST request with authentication
 */
export async function apiPost<T = any>(
  endpoint: string,
  body: any
): Promise<T> {
  const response = await authenticatedFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || 'API request failed');
  }

  return response.json();
}

/**
 * GET request with authentication
 */
export async function apiGet<T = any>(
  endpoint: string
): Promise<T> {
  const response = await authenticatedFetch(endpoint, {
    method: 'GET'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || 'API request failed');
  }

  return response.json();
}

/**
 * PUT request with authentication
 */
export async function apiPut<T = any>(
  endpoint: string,
  body: any
): Promise<T> {
  const response = await authenticatedFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || 'API request failed');
  }

  return response.json();
}

/**
 * DELETE request with authentication
 */
export async function apiDelete<T = any>(
  endpoint: string
): Promise<T> {
  const response = await authenticatedFetch(endpoint, {
    method: 'DELETE'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || 'API request failed');
  }

  return response.json();
}
